import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";

import { AccuracyFindingsTab } from "./AccuracyFindingsTab";
import { getWeeks, weeksKey, type FreshnessMeta } from "./api";
import { AssetDetail } from "./AssetDetail";
import { COMING_NEXT, ComingNext } from "./ComingNext";
import { FrictionTab } from "./FrictionTab";
import { ListingsTab } from "./ListingsTab";
import { PipelineTab } from "./PipelineTab";
import { StageDetail } from "./StageDetail";

/**
 * The freshness bridge (native-module contract step 2). `enabled` rather than
 * a conditional call: `useNativeModuleMeta` calls this on every render.
 */
export function useCvcePipelineMeta(enabled: boolean): FreshnessMeta | null | undefined {
  const head = useQuery({ queryKey: weeksKey, queryFn: getWeeks, enabled });
  return enabled ? head.data?.meta : undefined;
}

/**
 * CVCE Pipeline, rendered natively in the portal. `decision` is not read: the
 * shell has already wrapped this in `PermissionGate` (I-7). Stage detail is a
 * state of the Pipeline tab, not a tab of its own — it opens whenever the URL
 * carries a `stage` search param, on top of whichever tab is active.
 */
export function CvcePipelineModule({
  activeTabKey,
  areaName,
}: {
  decision: "edit" | "view" | "none";
  activeTabKey: string | null;
  areaName: string;
  ownerName?: string | null;
}) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const stage = typeof search.stage === "string" ? search.stage : null;
  const asset = typeof search.asset === "string" ? search.asset : null;

  if (activeTabKey === "accuracy_findings") {
    if (asset) return <AssetDetail areaName={areaName} />;
    return <AccuracyFindingsTab areaName={areaName} />;
  }
  // Friction never opens a stage detail, so it returns before the stage branch.
  if (activeTabKey === "friction") return <FrictionTab areaName={areaName} />;
  // Stage detail opens on top of whichever of Pipeline/Listings is active —
  // both read the same `/pipeline/week` tiles and both can open a stage.
  if (stage) return <StageDetail areaName={areaName} />;
  if (activeTabKey === "listings") return <ListingsTab areaName={areaName} />;
  if (activeTabKey && activeTabKey in COMING_NEXT) return <ComingNext tabKey={activeTabKey} />;
  return <PipelineTab areaName={areaName} />;
}
